import clsx from "clsx";
import {
  memo,
  type RefObject,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { HiOutlineDotsVertical } from "react-icons/hi";
import { MdBlock } from "react-icons/md";
import noImage from "@/assets/img/no-image.svg";
import { BOARD_CONFIG } from "@/config/browserBoard";
import type { Thread } from "@/entities/thread";
import {
  getImageUrl,
  getThreadBodyText,
  getThreadTitle,
  isR18ThreadTagName,
  isThreadExpired,
} from "@/entities/thread";
import { ThreadContextMenu } from "@/features/catalog/components/actions/ThreadContextMenu";
import { CatalogBoardIcon } from "@/features/catalog/components/CatalogBoardIcon";
import { CatalogReplyCount } from "@/features/catalog/components/lists/CatalogReplyCount";
import { CatalogThreadLink } from "@/features/catalog/components/navigation/CatalogThreadLink";
import { getThreadLinkAttributes } from "@/features/catalog/hooks/useFilteredThreads";
import {
  type CatalogEvent,
  isCatalogEvent,
} from "@/features/catalog/mediators/catalogEvents";
import {
  createCatalogItemMediator,
  isCatalogItemEvent,
} from "@/features/catalog/mediators/catalogItemMediator";
import { useCatalogStore } from "@/features/catalog/stores/catalogStore";
import {
  getCatalogImageMaskKind,
  getCatalogMaskVisibility,
} from "@/features/catalog/utils/catalogReveal";
import { getCatalogCategoryTags } from "@/features/catalog/utils/catalogTags";
import { useFavStore } from "@/features/fav-filter/stores/favStore";
import {
  unreadReplyCount,
  useHistoryStore,
  useViewedHistoryItem,
} from "@/features/history/stores/historyStore";
import { useNgStore } from "@/features/ng-filter/stores";
import { useSettingsStore } from "@/features/settings/hooks";
import { useLongPress } from "@/shared/hooks";
import { decorateTitle, resolveThumbnailBadge } from "@/shared/lib";
import { useAimogeBeforeRender, useAimogeRendered } from "@/shared/lib/aimoge";
import { ComponentScope } from "@/shared/ui/component-hierarchy/ComponentScope";
import {
  useComponentNode,
  useHierarchyLifetime,
  useMediatorSnapshot,
} from "@/shared/ui/component-hierarchy/hooks";
import { OekakiBadge, VideoBadge } from "@/shared/ui/media";
import { TagBadges, TextEffects } from "@/shared/ui/navigation";

interface CatalogItemProps {
  thread: Thread;
  isNew: boolean;
}

export const CatalogItem: React.FunctionComponent<CatalogItemProps> = memo(
  function CatalogItem(props: CatalogItemProps) {
    const renderedThread = useAimogeBeforeRender(
      "catalog:beforeRender",
      props.thread,
    );
    const elementRef = useAimogeRendered<HTMLElement>(
      "catalog",
      renderedThread,
      renderedThread?.id,
    );

    if (!renderedThread) return null;
    return (
      <CatalogItemPresenter
        thread={renderedThread}
        isNew={props.isNew}
        elementRef={elementRef}
      />
    );
  },
);

interface CatalogItemContentProps extends CatalogItemProps {
  elementRef: RefObject<HTMLElement | null>;
}

const CatalogItemPresenter: React.FunctionComponent<CatalogItemContentProps> =
  memo(function CatalogItemPresenter({
    thread,
    isNew,
    elementRef,
  }: CatalogItemContentProps) {
    const {
      textLines,
      imageSize,
      openInNewTab,
      showNew,
      showCount,
      showUnreadCount,
      catalogAnim,
      threadMenuOpenMethod,
    } = useCatalogStore();
    const showR18 = useSettingsStore((state) => state.showR18);
    const extendedTextEffects = useSettingsStore(
      (state) => state.extendedTextEffects,
    );
    const boardSlug = thread.b ?? BOARD_CONFIG.boardKey;
    const addViewed = useHistoryStore((state) => state.addViewed);
    const viewedItem = useViewedHistoryItem(boardSlug, thread.id);
    const isSuperFavorite = useFavStore(
      (state) => state.isThreadFavorite(thread) === 2,
    );
    const { isThreadHidden, showNgContent } = useNgStore();
    const isNg = showNgContent && isThreadHidden(thread);
    const isR18 = thread.tags.some((tag) => isR18ThreadTagName(tag.name));
    const hasFinePointer =
      typeof window !== "undefined" &&
      window.matchMedia("(pointer: fine)").matches;
    const itemModel = useMemo(
      () =>
        ({
          threadId: thread.id,
          boardSlug,
          isNg,
          isR18,
          hasAttachment: Boolean(thread.opPost.attachment),
          showR18,
          isViewed: viewedItem !== undefined,
          trackViewed: true,
          prefetchAvailable: false,
          menuOpenMethod: threadMenuOpenMethod,
          hasFinePointer,
        }) as const,
      [
        boardSlug,
        hasFinePointer,
        isNg,
        isR18,
        showR18,
        thread.id,
        thread.opPost.attachment,
        threadMenuOpenMethod,
        viewedItem,
      ],
    );
    const [mediator] = useState(() =>
      createCatalogItemMediator(itemModel, (effect) => {
        if (effect.type === "mark-viewed") {
          addViewed(effect.boardSlug, effect.threadId);
        }
      }),
    );
    useHierarchyLifetime(mediator);
    const node = useComponentNode((event) => {
      if (!isCatalogEvent(event) || !isCatalogItemEvent(event)) {
        return "unhandled";
      }
      return mediator.dispatch(event).status === "handled"
        ? "handled"
        : "unhandled";
    });
    const itemState = useMediatorSnapshot(mediator);
    const notify = (event: CatalogEvent): void => {
      node.dispatch(event);
    };

    // カタログでのアニメ画像が許可されている場合のみアニメ画像のみ動かし、それ以外の場合は動かさない
    const imageUrl = getImageUrl(
      thread.opPost.attachment,
      (catalogAnim === "always" ||
        (catalogAnim === "hover" && itemState.isHovered)) &&
        thread.opPost.attachment?.kind === "animated",
    );
    const accessibleTitle = decorateTitle(getThreadTitle(thread));
    const displayBody = decorateTitle(getThreadBodyText(thread));
    const categoryTags = getCatalogCategoryTags(thread.tags);
    const totalCount = thread.replyCount;
    const thumbnailBadge = resolveThumbnailBadge(thread.opPost.attachment);
    const threadLinkAttributes = getThreadLinkAttributes(openInNewTab);
    const isVideo = thumbnailBadge === "video";
    const isOekaki = thumbnailBadge === "oekaki";
    const isR18Hidden = itemState.isR18Hidden;
    const maskVisibility = getCatalogMaskVisibility({
      isNg,
      ngRevealed: itemState.isNgRevealed,
      isR18Hidden,
    });
    const maskKind = getCatalogImageMaskKind({
      isNg,
      ngRevealed: itemState.isNgRevealed,
      isR18Hidden,
    });

    // 既読判定
    const isViewed = itemState.isViewed;
    const isExpired = isThreadExpired(thread);

    useLayoutEffect(() => {
      mediator.dispatch({
        type: "catalog/item-synchronized",
        ...itemModel,
      });
    }, [mediator, itemModel]);

    const unreadCount = viewedItem
      ? unreadReplyCount(viewedItem.readReplyNumber, totalCount)
      : undefined;

    return (
      <ComponentScope node={node}>
        <CatalogItemView
          thread={thread}
          isNew={isNew}
          elementRef={elementRef}
          textLines={textLines}
          imageSize={imageSize}
          showNew={showNew}
          showCount={showCount}
          showUnreadCount={showUnreadCount}
          extendedTextEffects={extendedTextEffects}
          imageUrl={imageUrl}
          accessibleTitle={accessibleTitle}
          displayBody={displayBody}
          categoryTags={categoryTags}
          totalCount={totalCount}
          unreadCount={unreadCount}
          threadLinkAttributes={threadLinkAttributes}
          isVideo={isVideo}
          isOekaki={isOekaki}
          isNg={itemState.isNg}
          isNgRevealed={itemState.isNgRevealed}
          isR18Hidden={itemState.isR18Hidden}
          maskVisibility={maskVisibility}
          maskKind={maskKind}
          isViewed={isViewed}
          isSuperFavorite={isSuperFavorite}
          isExpired={isExpired}
          longPressOpensMenu={itemState.longPressOpensMenu}
          isMenuButtonDisplayed={itemState.isMenuButtonDisplayed}
          menuOpen={itemState.isMenuOpen}
          onEvent={notify}
        />
      </ComponentScope>
    );
  });

export interface CatalogItemViewProps {
  readonly thread: Thread;
  readonly isNew: boolean;
  readonly elementRef: RefObject<HTMLElement | null>;
  readonly textLines: number;
  readonly imageSize: number;
  readonly showNew: boolean;
  readonly showCount: boolean;
  readonly showUnreadCount: boolean;
  readonly extendedTextEffects: boolean;
  readonly imageUrl: string;
  readonly accessibleTitle: string;
  readonly displayBody: string;
  readonly categoryTags: ReturnType<typeof getCatalogCategoryTags>;
  readonly totalCount: number;
  readonly unreadCount: number | undefined;
  readonly threadLinkAttributes: ReturnType<typeof getThreadLinkAttributes>;
  readonly isVideo: boolean;
  readonly isOekaki: boolean;
  readonly isNg: boolean;
  readonly isNgRevealed: boolean;
  readonly isR18Hidden: boolean;
  readonly maskVisibility: ReturnType<typeof getCatalogMaskVisibility>;
  readonly maskKind: ReturnType<typeof getCatalogImageMaskKind>;
  readonly isViewed: boolean;
  readonly isSuperFavorite: boolean;
  readonly isExpired: boolean;
  readonly longPressOpensMenu: boolean;
  readonly isMenuButtonDisplayed: boolean;
  readonly menuOpen: boolean;
  readonly onEvent: (event: CatalogEvent) => void;
}

export function CatalogItemView({
  thread,
  isNew,
  elementRef,
  textLines,
  imageSize,
  showNew,
  showCount,
  showUnreadCount,
  extendedTextEffects,
  imageUrl,
  accessibleTitle,
  displayBody,
  categoryTags,
  totalCount,
  unreadCount,
  threadLinkAttributes,
  isVideo,
  isOekaki,
  isNg,
  isNgRevealed,
  isR18Hidden,
  maskVisibility,
  maskKind,
  isViewed,
  isSuperFavorite,
  isExpired,
  longPressOpensMenu,
  isMenuButtonDisplayed,
  menuOpen,
  onEvent,
}: CatalogItemViewProps): React.ReactElement {
  const thumbnailLinkRef = useRef<HTMLAnchorElement>(null);
  const r18TriggerRef = useRef<HTMLButtonElement>(null);
  const pendingFocusRef = useRef<"thumbnail" | "r18" | null>(null);
  const longPressHandlers = useLongPress({
    onLongPress: (): void => onEvent({ type: "catalog/item-menu-opened" }),
  });
  const handleR18Click = (event: React.MouseEvent): void => {
    event.preventDefault();
    event.stopPropagation();
    pendingFocusRef.current = "thumbnail";
    onEvent({ type: "catalog/item-r18-revealed" });
  };
  const handleThreadClick = (event: React.MouseEvent): void => {
    if (maskKind === "ng") {
      event.preventDefault();
      event.stopPropagation();
      if (isR18Hidden) pendingFocusRef.current = "r18";
    }
    onEvent({ type: "catalog/item-activated" });
  };
  useEffect(() => {
    if (pendingFocusRef.current === "thumbnail" && !isR18Hidden) {
      pendingFocusRef.current = null;
      thumbnailLinkRef.current?.focus();
    } else if (
      pendingFocusRef.current === "r18" &&
      maskKind === "r18" &&
      isNgRevealed
    ) {
      pendingFocusRef.current = null;
      r18TriggerRef.current?.focus();
    }
  }, [isNgRevealed, isR18Hidden, maskKind]);

  const thumbnail = (
    <div
      className={clsx(
        "relative aspect-square bg-muted flex items-center justify-center catalog-configured-thumb",
        isNg &&
          isNgRevealed &&
          !isR18Hidden &&
          "after:absolute after:inset-0 after:cursor-pointer after:bg-transparent after:content-['']",
      )}
      style={{
        width: "100%",
        maxHeight: `${imageSize}px`,
      }}
    >
      <img
        src={imageUrl}
        alt={accessibleTitle}
        loading="lazy"
        className={clsx(
          "w-full h-full object-contain",
          maskKind === "ng" && "blur-xl opacity-20",
          maskKind === "r18" && "r18-spoiler-image",
        )}
        onError={(e: React.SyntheticEvent<HTMLImageElement>): void => {
          (e.target as HTMLImageElement).src = noImage;
        }}
      />

      {isVideo && !maskKind && <VideoBadge />}
      {isOekaki && maskKind !== "ng" && <OekakiBadge />}
      <CatalogReplyCount
        maskKind={maskKind}
        showCount={showCount}
        showUnreadCount={showUnreadCount}
        totalCount={totalCount}
        unreadCount={unreadCount}
      />
      {showNew && isNew && !maskKind && (
        <strong className="absolute top-1 left-1 px-1.5 py-0.5 text-xs font-bold bg-primary text-primary-foreground rounded">
          NEW
        </strong>
      )}
      {maskKind === "ng" ? (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-background/95"
          aria-hidden="true"
        >
          <strong className="flex items-center gap-1 px-3 py-2 bg-destructive/90 text-destructive-foreground rounded text-sm font-bold">
            <MdBlock aria-hidden="true" size={16} />
            NG
          </strong>
          <small className="text-xs text-muted-foreground">タップで表示</small>
        </div>
      ) : null}
      {isNg && isNgRevealed && !isR18Hidden && (
        <strong className="absolute top-1 left-1 px-2 py-1 bg-destructive/90 text-destructive-foreground rounded text-xs font-bold flex items-center gap-1 pointer-events-none">
          <MdBlock aria-hidden="true" size={12} />
          NG
        </strong>
      )}
    </div>
  );

  return (
    <>
      <article
        ref={elementRef}
        data-catalog-layout-key={thread.id}
        aria-label={accessibleTitle}
        className={clsx(
          "group relative border border-transparent bg-card/50 rounded-lg overflow-hidden hover:bg-card transition-colors",
          isViewed && "catalog-item-viewed",
          isSuperFavorite && "catalog-item-super-favorite",
          isExpired && "catalog-item-expired",
        )}
      >
        <figure
          className="catalog-thumbnail-shell"
          style={{
            width: `min(100%, ${imageSize}px)`,
            maxHeight: `${imageSize}px`,
          }}
        >
          {maskKind === "r18" && (
            <button
              ref={r18TriggerRef}
              type="button"
              className="catalog-r18-spoiler-trigger"
              aria-label={`${accessibleTitle}のR18画像を表示`}
              onMouseEnter={(): void =>
                onEvent({ type: "catalog/item-hover-changed", hovered: true })
              }
              onMouseLeave={(): void =>
                onEvent({ type: "catalog/item-hover-changed", hovered: false })
              }
              onFocus={(): void =>
                onEvent({ type: "catalog/item-hover-changed", hovered: true })
              }
              onBlur={(): void =>
                onEvent({ type: "catalog/item-hover-changed", hovered: false })
              }
              {...(longPressOpensMenu ? longPressHandlers : {})}
              onClick={handleR18Click}
            />
          )}
          <CatalogThreadLink
            ref={thumbnailLinkRef}
            thread={thread}
            className="block"
            {...threadLinkAttributes}
            aria-label={
              maskKind === "ng" ? `${accessibleTitle}のNG画像を表示` : undefined
            }
            onMouseEnter={(): void =>
              onEvent({ type: "catalog/item-hover-changed", hovered: true })
            }
            onMouseLeave={(): void =>
              onEvent({ type: "catalog/item-hover-changed", hovered: false })
            }
            onFocus={(): void =>
              onEvent({ type: "catalog/item-hover-changed", hovered: true })
            }
            onBlur={(): void =>
              onEvent({ type: "catalog/item-hover-changed", hovered: false })
            }
            {...(longPressOpensMenu ? longPressHandlers : {})}
            onClick={handleThreadClick}
          >
            {thumbnail}
          </CatalogThreadLink>
        </figure>
        {(textLines > 0 || isMenuButtonDisplayed) && (
          <header
            className={clsx(
              "flex items-start px-2",
              textLines > 0 ? "pt-2 mb-2" : "pt-0 mb-0",
            )}
          >
            {textLines > 0 && (
              <CatalogThreadLink
                thread={thread}
                {...threadLinkAttributes}
                aria-label={
                  maskKind === "ng"
                    ? `${accessibleTitle}のNGスレッド情報を表示`
                    : undefined
                }
                onMouseEnter={(): void =>
                  onEvent({
                    type: "catalog/item-hover-changed",
                    hovered: true,
                  })
                }
                onMouseLeave={(): void =>
                  onEvent({
                    type: "catalog/item-hover-changed",
                    hovered: false,
                  })
                }
                onFocus={(): void =>
                  onEvent({
                    type: "catalog/item-hover-changed",
                    hovered: true,
                  })
                }
                onBlur={(): void =>
                  onEvent({
                    type: "catalog/item-hover-changed",
                    hovered: false,
                  })
                }
                className={clsx(
                  "catalog-item-text catalog-text-clamp min-w-0 flex-1 text-xs text-muted-foreground leading-tight",
                  "group-hover:text-foreground transition-colors",
                  maskVisibility.text && "blur-sm",
                )}
                style={
                  {
                    "--catalog-text-lines": textLines,
                  } as React.CSSProperties
                }
                {...(longPressOpensMenu ? longPressHandlers : {})}
                onClick={handleThreadClick}
              >
                {extendedTextEffects ? (
                  <TextEffects text={displayBody} extended />
                ) : (
                  displayBody
                )}
              </CatalogThreadLink>
            )}
            {isMenuButtonDisplayed && (
              <button
                type="button"
                onClick={(): void =>
                  onEvent({ type: "catalog/item-menu-opened" })
                }
                className="flex-shrink-0 flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ml-auto"
                aria-label="スレッドメニュー"
              >
                <HiOutlineDotsVertical className="w-5 h-5" />
              </button>
            )}
          </header>
        )}
        <footer className="catalog-item-tags-row flex items-start gap-1 px-2 pb-2">
          <TagBadges
            tags={categoryTags}
            className="catalog-tags min-w-0 flex-1"
          />
          <CatalogBoardIcon slug={thread.b} className="ml-auto" />
        </footer>
      </article>

      <ThreadContextMenu
        thread={thread}
        isOpen={menuOpen}
        onClose={(): void => onEvent({ type: "catalog/item-menu-closed" })}
      />
    </>
  );
}
